import { create } from 'zustand';
import { extractNodes, extractEdges } from '../../../utils/CanvasUtil';
import { EDGE_TYPES } from '../../../utils/CanvasConstants';
import { v4 as uuid } from 'uuid';

const useCanvasStore = create((set, get) => ({
  nodes: [],
  edges: [],
  draggingNodes: [],
  codePaneNavigation: { componentId: null, sequence: 0 },

  requestCodePaneNavigation: componentId =>
    set(s => ({
      codePaneNavigation: {
        componentId,
        sequence: componentId
          ? s.codePaneNavigation.sequence + 1
          : s.codePaneNavigation.sequence,
      },
    })),

  addNode: node => {
    if (!node) return;
    set(s => ({
      nodes: [...s.nodes, node],
      draggingNodes: [...s.draggingNodes, node],
    }));
  },

  addDraggingNode: draggingNode => {
    set(s => ({ draggingNodes: [...s.draggingNodes, draggingNode] }));
  },

  addEdge: edge => {
    set(s => ({ edges: [...s.edges, edge] }));
  },

  removeLoopEdgeBySourceAndTargetId: (sourceComponentId, targetComponentId) => {
    set(s => ({
      edges: s.edges.filter(edge => {
        const isLoopEdge = edge.type === EDGE_TYPES.LOOPLINK;
        const isSourceIdEqual = edge.source === sourceComponentId;
        const isTargetIdEqual = edge.target === targetComponentId;
        return !(isLoopEdge && isSourceIdEqual && isTargetIdEqual);
      }),
    }));
  },

  addNodes: nodes => {
    if (!nodes) return;
    set(s => {
      const updatedNodesList = [...s.nodes, ...nodes];
      return { nodes: updatedNodesList, draggingNodes: updatedNodesList };
    });
  },

  addEdges: edges => {
    if (!edges) return;
    set(s => ({ edges: [...s.edges, ...edges] }));
  },

  setNodes: nodes => {
    set({ nodes, draggingNodes: nodes });
  },

  setNodesFn: updateFn => {
    set(s => {
      const updatedNodes = updateFn(s.nodes);
      return { nodes: updatedNodes, draggingNodes: updatedNodes };
    });
  },

  setNodesFromDraggingNodesFn: updateFn => {
    set(s => {
      const updatedNodes = updateFn(s.draggingNodes);
      return { nodes: updatedNodes, draggingNodes: updatedNodes };
    });
  },

  setDraggingNodes: draggingNodes => {
    set({ draggingNodes });
  },

  setDraggingNodesFn: updateFn => {
    set(s => ({ draggingNodes: updateFn(s.draggingNodes) }));
  },

  setEdges: edges => {
    set({ edges });
  },

  setEdgesFn: updateFn => {
    set(s => ({ edges: updateFn(s.edges) }));
  },

  setNodesAndEdges: elements => {
    const extractedNodes = extractNodes(elements);
    const extractedEdges = extractEdges(elements);
    set({ nodes: extractedNodes, draggingNodes: extractedNodes, edges: extractedEdges });
  },

  updateNode: updatedNode => {
    set(s => {
      const nodeIndex = s.nodes.findIndex(node => node.id === updatedNode.id);
      if (nodeIndex >= 0) {
        const newNodes = [...s.nodes];
        const newDragging = [...s.draggingNodes];
        newNodes[nodeIndex] = updatedNode;
        newDragging[nodeIndex] = updatedNode;
        return { nodes: newNodes, draggingNodes: newDragging };
      }
      return {};
    });
  },

  setFormRedirectEdges: (sourceComponentId, linkedComponents) => {
    set(s => {
      const nonFormDirectEdges = s.edges.filter(edge => {
        const isFormRedirectEdge = edge.type === EDGE_TYPES.FORMREDIRECTLINK;
        const isSourceIdEqual = edge.source === sourceComponentId;
        return !(isFormRedirectEdge && isSourceIdEqual);
      });

      if (linkedComponents && linkedComponents.length > 0) {
        const newFormRedirectEdges = linkedComponents.map(linkedComponent => ({
          id: uuid(),
          source: sourceComponentId,
          target: linkedComponent.componentId,
          type: EDGE_TYPES.FORMREDIRECTLINK,
        }));
        return { edges: [...nonFormDirectEdges, ...newFormRedirectEdges] };
      } else {
        return { edges: nonFormDirectEdges };
      }
    });
  },

  syncNodes: () => {
    set(s => ({ nodes: s.draggingNodes }));
  },

  // AI active component tracking — components currently being worked on by the AI agent
  aiActiveComponentIds: new Set(),

  addAiActiveComponent: (componentId) => {
    if (!componentId) return;
    set(s => {
      const next = new Set(s.aiActiveComponentIds);
      next.add(componentId);
      return { aiActiveComponentIds: next };
    });
  },

  removeAiActiveComponent: (componentId) => {
    if (!componentId) return;
    set(s => {
      const next = new Set(s.aiActiveComponentIds);
      next.delete(componentId);
      return { aiActiveComponentIds: next };
    });
  },

  clearAiActiveComponents: () => {
    set({ aiActiveComponentIds: new Set() });
  },
}));

export default useCanvasStore;
